import React from 'react';
import { 
  Box, 
  Heading, 
  SimpleGrid, 
  Text, 
  Stat, 
  StatLabel, 
  StatNumber, 
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Skeleton,
  useColorModeValue
} from '@chakra-ui/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { CHART_COLORS } from '../utils/constants';
import { formatUSD, formatTokenAmount, formatPercentage } from '../utils/formatters';
import { prepareChartData } from '../utils/calculations';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        bg="yakuza.black"
        p={3}
        borderRadius="md"
        border="1px solid"
        borderColor="yakuza.red"
        boxShadow="md"
      >
        <Text fontWeight="bold" color="yakuza.white">
          {data.name}
        </Text>
        <Text color="yakuza.gold">
          {formatTokenAmount(data.value)} YAKUZA
        </Text>
        <Text color="yakuza.white" fontSize="sm">
          {formatPercentage(data.percentage / 100)}
        </Text>
        <Text color="yakuza.white" fontSize="xs" mt={1}>
          {data.description}
        </Text>
      </Box>
    );
  }
  return null;
};

const TokenomicsChart = ({ tokenomics, isLoading }) => {
  const chartData = tokenomics ? prepareChartData(tokenomics) : [];
  const colorKeys = Object.keys(CHART_COLORS);
  
  // Format some values for display
  const tokenPrice = tokenomics?.metrics?.initialTokenPriceUsd || 0;
  const marketCap = tokenomics?.metrics?.initialMarketCap || 0;
  const liquidityValue = tokenomics?.metrics?.liquidityPoolUsdValue || 0;
  const totalSupply = tokenomics?.totalSupply || 0;
  
  // Background styles
  const cardBg = useColorModeValue('rgba(26, 26, 26, 0.8)', 'rgba(26, 26, 26, a0.8)');
  const borderColor = useColorModeValue('yakuza.red', 'yakuza.red');
  
  return (
    <Box>
      <Heading as="h2" variant="title" mb={6}>
        Tokenomics Overview
      </Heading>
      
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Chart Card */}
        <Box 
          bg={cardBg}
          borderRadius="md"
          borderWidth="1px"
          borderColor={borderColor}
          p={6}
          boxShadow="lg"
          position="relative"
          overflow="hidden"
        >
          <Heading as="h3" size="md" color="yakuza.gold" mb={4}>
            Token Distribution
          </Heading>
          
          <Skeleton 
            isLoaded={!isLoading && chartData.length > 0} 
            height={isLoading || chartData.length === 0 ? "300px" : "auto"}
            startColor="yakuza.gray" 
            endColor="yakuza.lightGray"
          >
            <Box height="300px">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={60}
                    paddingAngle={2}
                    dataKey="value"
                    labelLine={false}
                  >
                    {chartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={CHART_COLORS[colorKeys[index % colorKeys.length]]} 
                        stroke="#1a1a1a"
                        strokeWidth={1}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    formatter={(value) => <span style={{color: '#f5f5f5'}}>{value}</span>}
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                    wrapperStyle={{paddingLeft: "20px"}}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Skeleton>
          
          <Box mt={6}>
            <Text color="yakuza.white" fontSize="sm">
              Total Supply: {formatTokenAmount(totalSupply)} YAKUZA
            </Text>
          </Box>
        </Box>
        
        {/* Metrics Card */}
        <Box 
          bg={cardBg}
          borderRadius="md"
          borderWidth="1px"
          borderColor={borderColor}
          p={6}
          boxShadow="lg"
        >
          <Heading as="h3" size="md" color="yakuza.gold" mb={4}>
            Launch Metrics
          </Heading>
          
          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={6}>
            <Stat>
              <StatLabel color="yakuza.gold">Token Price at Launch</StatLabel>
              <Skeleton isLoaded={!isLoading} startColor="yakuza.gray" endColor="yakuza.lightGray">
                <StatNumber color="yakuza.white">{formatUSD(tokenPrice)}</StatNumber>
                <StatHelpText color="yakuza.white">Per YAKUZA token</StatHelpText>
              </Skeleton>
            </Stat>
            
            <Stat>
              <StatLabel color="yakuza.gold">Initial Market Cap</StatLabel>
              <Skeleton isLoaded={!isLoading} startColor="yakuza.gray" endColor="yakuza.lightGray">
                <StatNumber color="yakuza.white">{formatUSD(marketCap)}</StatNumber>
                <StatHelpText color="yakuza.white">At launch</StatHelpText>
              </Skeleton>
            </Stat>
            
            <Stat>
              <StatLabel color="yakuza.gold">Liquidity Value</StatLabel>
              <Skeleton isLoaded={!isLoading} startColor="yakuza.gray" endColor="yakuza.lightGray">
                <StatNumber color="yakuza.white">{formatUSD(liquidityValue)}</StatNumber>
                <StatHelpText color="yakuza.white">ETH + YAKUZA in pool</StatHelpText>
              </Skeleton>
            </Stat>
            
            <Stat>
              <StatLabel color="yakuza.gold">Tokens per ETH</StatLabel>
              <Skeleton isLoaded={!isLoading} startColor="yakuza.gray" endColor="yakuza.lightGray">
                <StatNumber color="yakuza.white">
                  {formatTokenAmount(tokenomics?.metrics?.tokensPerEth || 0)}
                </StatNumber>
                <StatHelpText color="yakuza.white">YAKUZA per 1 ETH</StatHelpText>
              </Skeleton>
            </Stat>
          </SimpleGrid>
          
          {/* Allocation Table */}
          <Box mt={8}>
            <Heading as="h4" size="sm" color="yakuza.white" mb={4}>
              Token Allocation
            </Heading>
            
            <Skeleton 
              isLoaded={!isLoading && tokenomics} 
              startColor="yakuza.gray" 
              endColor="yakuza.lightGray"
            >
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color="yakuza.gold" borderColor="yakuza.red">Category</Th>
                      <Th color="yakuza.gold" borderColor="yakuza.red" isNumeric>Percentage</Th>
                      <Th color="yakuza.gold" borderColor="yakuza.red" isNumeric>Tokens</Th>
                      <Th color="yakuza.gold" borderColor="yakuza.red" isNumeric>Value (USD)</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {tokenomics && Object.keys(tokenomics.distribution).map(key => (
                      <Tr key={key}>
                        <Td borderColor="rgba(196, 30, 58, 0.3)" color="yakuza.white">
                          {key.charAt(0).toUpperCase() + key.slice(1)}
                        </Td>
                        <Td borderColor="rgba(196, 30, 58, 0.3)" color="yakuza.white" isNumeric>
                          {formatPercentage(tokenomics.distribution[key].percentage / 100)}
                        </Td>
                        <Td borderColor="rgba(196, 30, 58, 0.3)" color="yakuza.white" isNumeric>
                          {formatTokenAmount(tokenomics.distribution[key].tokens)}
                        </Td>
                        <Td borderColor="rgba(196, 30, 58, 0.3)" color="yakuza.white" isNumeric>
                          {formatUSD(
                            tokenomics.distribution[key].tokens * 
                            tokenomics.metrics.initialTokenPriceUsd
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Skeleton>
          </Box>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default TokenomicsChart;